// Нативные подсказки браузера (атрибут `title`) выглядят чужеродно рядом с
// antd Tooltip. Компонент перехватывает наведение на любой элемент с `title`,
// переносит текст в `data-native-title` (браузер свою подсказку уже не покажет)
// и рисует вместо неё antd Tooltip в стиле темы. Работает и для `title`,
// который ставит сам antd (Badge, Pagination, Select).
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Tooltip } from 'antd';

interface TooltipAnchor {
  text: string;
  top: number;
  left: number;
  width: number;
  height: number;
}

const SHOW_DELAY_MS = 300;
const NATIVE_TITLE_ATTR = 'data-native-title';

export function NativeTitleTooltips(): ReactElement | null {
  const [anchor, setAnchor] = useState<TooltipAnchor | null>(null);
  const timerRef = useRef<number | undefined>(undefined);
  const currentRef = useRef<Element | null>(null);

  useEffect(() => {
    const hide = (): void => {
      window.clearTimeout(timerRef.current);
      currentRef.current = null;
      setAnchor(null);
    };

    const onMouseOver = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const element = target.closest(`[title], [${NATIVE_TITLE_ATTR}]`);
      if (!element || element === currentRef.current) return;
      if (element.closest('.ant-tooltip')) return;

      const rawTitle = element.getAttribute('title');
      if (rawTitle !== null) {
        if (rawTitle.trim() === '') return;
        element.setAttribute(NATIVE_TITLE_ATTR, rawTitle);
        element.removeAttribute('title');
        // Без title у иконки без текста пропало бы доступное имя — сохраняем его.
        if (!element.hasAttribute('aria-label') && !element.textContent?.trim()) {
          element.setAttribute('aria-label', rawTitle);
        }
      }

      const text = element.getAttribute(NATIVE_TITLE_ATTR);
      if (!text) return;

      const visibleText = element.textContent?.trim();
      if (text.trim() === visibleText && element.scrollWidth <= element.clientWidth) {
        hide();
        return;
      }

      window.clearTimeout(timerRef.current);
      currentRef.current = element;
      setAnchor(null);
      timerRef.current = window.setTimeout(() => {
        if (currentRef.current !== element || !element.isConnected) return;
        const rect = element.getBoundingClientRect();
        setAnchor({
          text,
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      }, SHOW_DELAY_MS);
    };

    const onMouseOut = (event: MouseEvent): void => {
      const element = currentRef.current;
      if (!element) return;
      const next = event.relatedTarget;
      if (next instanceof Node && element.contains(next)) return;
      hide();
    };

    document.addEventListener('mouseover', onMouseOver, true);
    document.addEventListener('mouseout', onMouseOut, true);
    document.addEventListener('mousedown', hide, true);
    window.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('mouseover', onMouseOver, true);
      document.removeEventListener('mouseout', onMouseOut, true);
      document.removeEventListener('mousedown', hide, true);
      window.removeEventListener('scroll', hide, true);
      window.clearTimeout(timerRef.current);
    };
  }, []);

  if (!anchor) return null;

  return (
    <Tooltip open title={anchor.text}>
      <div
        aria-hidden="true"
        data-testid="native-title-tooltip-anchor"
        style={{
          position: 'fixed',
          top: anchor.top,
          left: anchor.left,
          width: anchor.width,
          height: anchor.height,
          pointerEvents: 'none',
        }}
      />
    </Tooltip>
  );
}
